<template>
  <div>
    <div
      class="action-badge d-flex align-items-center text-small-1 pointer"
      id="open-notification-center"
      tabindex="0"
      @click="showPopover = true"
    >
      <i class="notification-icon" :class="notificationIconClass"></i>
      <span class="count ml-1 bold" v-if="notificationCount">{{ notificationCount }}</span>
    </div>
    <full-page-modal @cancel="closePopover" v-if="showPopover">
      <inbox :org-id="orgId" />
    </full-page-modal>
    <!--    <b-popover
      ref="popover"
      custom-class="notification-center"
      target="open-notification-center"
      placement="bottom"
      triggers="click"
      :show.sync="showPopover"
    >
      <div class="notifications">
        <notifications-list
          :org-id="orgId"
          v-if="showPopover"
          @notification-click="onNotificationClick"
        />
      </div>
    </b-popover>-->
  </div>
</template>

<script>
import FullPageModal from '@/components/FullPageModal.vue'
import { ENTITY_MAP } from '@/constants/entities'
import { notificationMutation } from '@/constants/mutations'
import { ROUTE_NAMES } from '@/constants/routes'
import cron from '@/services/cron'
import { MILLIS_IN_A_MINUTE } from '@/utils/formatDate'
import getEntityTypeFromNotification from '@/utils/getEntityTypeFromNotification'
import handleClickNavigation from '@/utils/handleClickNavigation'
import Inbox from '@/views/Inbox.vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import * as keymapService from '@/services/keymapService/keymapService'

const pollingInterval = {
  TAB_FOCUS: 60 * 1000, // 1 minute
  TAB_BLUR: MILLIS_IN_A_MINUTE * 60 // 1 hour
}

/**
 * This is the component for NotificationCenter. It can show different kinds of notifications
 * - Mentions in initiative description, initiative comment, goal comment
 */
export default {
  name: 'NotificationCenter',
  props: ['orgId'],
  components: {
    FullPageModal,
    Inbox
  },
  data() {
    return {
      showPopover: false
    }
  },
  computed: {
    notificationSummary() {
      return R.pathOr(
        {},
        ['notificationCenter', this.orgId, 'summary'],
        this.$store.state.notification
      )
    },
    notificationCount() {
      return R.prop('count', this.notificationSummary)
    },
    notificationIconClass() {
      const classes = ['notification-icon']
      const iconClass = this.notificationCount > 0 ? 'bell-icon' : 'bell-empty-icon'
      classes.push(iconClass)
      return classes.join(' ')
    }
  },
  methods: {
    ...mapActions('notification', ['getNotificationSummary']),
    async checkForNewNotifications() {
      const previousSummary = this.notificationSummary
      const currentSummary = await this.getNotificationSummary({ orgId: this.orgId })
      if (!R.equals(previousSummary, currentSummary)) {
        this.$store.commit(`notification/${notificationMutation.SET_NOTIFICATIONS_LIST}`, {
          orgId: this.orgId
        })
      }
    },
    getNotificationEntityLocation(notification) {
      const entityType = getEntityTypeFromNotification(notification)
      const entity = ENTITY_MAP[entityType]
      const eventId = R.path(['data', 'event', 'id'], notification)
      const params = { orgId: notification.organizationId }
      switch (entity.routeName) {
        case ROUTE_NAMES.GOAL:
          params.goalId = R.path(['data', 'goal', 'id'], notification)
          break
        case ROUTE_NAMES.KR:
          {
            const kr = R.pathOr({}, ['data', 'keyResult'], notification)
            params.goalId = kr.goalId
            params.krId = kr.id
          }
          break
        case ROUTE_NAMES.INITIATIVE:
          {
            const initiative = R.pathOr({}, ['data', 'initiative'], notification)
            params.goalId = initiative.goalId
            params.krId = initiative.keyResultId
            params.initiativeId = initiative.id
          }
          break
      }
      return {
        name: entity.routeName,
        params,
        query: {
          event: eventId
        }
      }
    },
    onNotificationClick({ notification, event }) {
      this.closePopover()
      const location = this.getNotificationEntityLocation(notification)
      handleClickNavigation.call(this, event, location)
    },
    openPopover() {
      this.showPopover = true
    },
    closePopover() {
      this.showPopover = false
    },
    isInput(tagName) {
      return ['INPUT', 'TEXTAREA'].includes(tagName)
    },
    onClose(event) {
      !this.isInput(event.target.tagName) && this.closePopover()
    }
  },
  mounted() {
    this.checkForNewNotifications()
    this.cron = cron.start(
      event => {
        if (event === cron.event.TAB_FOCUSSED) {
          this.getNotificationSummary({ orgId: this.orgId })
        } else {
          this.checkForNewNotifications()
        }
      },
      {
        interval: pollingInterval.TAB_FOCUS,
        tabBlurInterval: pollingInterval.TAB_BLUR,
        blurRefetchTimeout: 1000 * 5
      }
    )
    keymapService.activateCategory('inbox', {
      'open-inbox': this.openPopover,
      'close-inbox': this.onClose
    })
  },
  beforeDestroy() {
    this.cron.stop()
    keymapService.deactivateCategory('inbox')
  },
  watch: {
    $route(from, to) {
      if (from.params.orgId !== to.params.orgId || from.query.teamId !== to.query.teamId) {
        this.checkForNewNotifications()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.action-badge {
  background: $brandSecondary;
  border-radius: 20px;
  display: flex;
  padding: 5px 10px;
  position: relative;

  &:focus {
    outline: none;
  }
}

.count {
  border-radius: 15px;
  flex-shrink: 0;
  min-width: 21px;
  text-align: center;
  line-height: 15px;
  position: absolute;
  top: -8px;
  right: -7px;
  background-color: $error;
  border: 2px solid $documentColor;
  color: $documentColor;
  padding: 0 3px;
}

.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.notification-icon {
  @include dimension(15px, 15px);
}

.link-btn {
  padding: 6px 12px;

  &:hover {
    text-decoration: underline;
  }
}
.notifications {
  max-height: 80vh;
  overflow: auto;
  @include scrollbarWidth($scrollSecondary);
}
</style>
<style lang="scss">
.notification-center.b-popover {
  width: 540px;
  max-width: initial;

  .popover-body {
    padding: 0;
  }
}
</style>
